import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Import before/after images
import beforeImage1 from '../../assets/images/Before/Before1.jpeg';
import beforeImage2 from '../../assets/images/Before/Before2.jpeg';
import beforeImage3 from '../../assets/images/Before/Before3.jpeg';
import beforeImage4 from '../../assets/images/Before/Before4.jpeg';

import afterImage1 from '../../assets/images/After/After1.jpeg';
import afterImage2 from '../../assets/images/After/After2.jpeg';
import afterImage3 from '../../assets/images/After/After3.jpeg';
import afterImage4 from '../../assets/images/After/After4.jpeg';

// Avatar image for all testimonials
import avatarImage from '../../assets/images/avatar.jpeg';

const ReviewsSection = styled.section`
  padding: 80px 0;
  background-color: #f1f1f1;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ReviewsTitle = styled.h2`
  font-size: 64px;
  font-weight: bold;
  margin-bottom: 40px;
  text-align: ${props => props.$dir === 'rtl' ? 'right' : 'left'};
  padding-bottom: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  .trusted-text {
    color: #3858F9;
  }
  
  .care-text {
    color: #333;
  }
  
  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const BeforeAfterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BeforeAfterCard = styled.div`
  position: relative;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const CardImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
`;

const ToggleContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  background-color: #ffffff;
  border-radius: 50px;
  padding: 3px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 140px;
  cursor: pointer;
`;

const ToggleOption = styled.div`
  flex: 1;
  text-align: center;
  padding: 6px 0;
  font-size: 14px;
  font-weight: bold;
  z-index: 2;
  transition: color 0.3s;
  color: ${props => props.$isActive ? '#ffffff' : '#000000'};
  text-shadow: ${props => props.$isActive ? '0px 0px 1px rgba(255,255,255,0.5)' : 'none'};
`;

const ToggleSlider = styled.div`
  position: absolute;
  width: 50%;
  height: 85%;
  top: 7%;
  left: ${props => props.$position === 'after' ? '3px' : '50%'};
  background-color: #3858F9;
  border-radius: 50px;
  transition: left 0.3s ease;
  z-index: 1;
`;

const TestimonialsContainer = styled.div`
  position: relative;
`;

const TestimonialsHeader = styled.h3`
  text-transform: uppercase;
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin-bottom: 30px;
  text-align: ${props => props.dir === 'rtl' ? 'right' : 'left'};
`;

const TestimonialsWrapper = styled.div`
  position: relative;
  overflow: hidden;
  padding-bottom: 30px;
`;

const TestimonialsTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease;
  transform: translateX(${props => props.$dir === 'rtl' ? (props.$activeIndex * 100) : -(props.$activeIndex * 100)}%);
`;

const TestimonialItem = styled.div`
  flex: 0 0 100%;
  max-width: 100%;
  padding-right: ${props => props.$dir === 'rtl' ? '0' : '20px'};
  padding-left: ${props => props.$dir === 'rtl' ? '20px' : '0'};
  display: flex;
  align-items: flex-start;
  
  /* Remove the 50% width to prevent empty pages in carousel */
`;

const TestimonialProfile = styled.div`
  margin-right: ${props => props.$dir === 'rtl' ? '0' : '20px'};
  margin-left: ${props => props.$dir === 'rtl' ? '20px' : '0'};
`;

const ProfileImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
`;

const TestimonialContent = styled.div`
  flex: 1;
`;

const ClientName = styled.h4`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 5px;
`;

const TestimonialText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
`;

const NavigationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  direction: ${props => props.$dir};
`;

const NavButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 5px;
  cursor: pointer;
  color: #333;
  font-size: 20px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background-color: #f0f0f0;
  }
`;

const ProgressIndicators = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
`;

const ProgressDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$isActive ? '#3858F9' : '#ddd'};
  margin: 0 5px;
`;

// Sample data for before/after treatments
const treatmentData = [
  {
    id: 1,
    treatmentName: 'SMILE MAKEOVER',
    beforeImage: beforeImage1,
    afterImage: afterImage1,
  },
  {
    id: 2,
    treatmentName: 'TEETH WHITENING',
    beforeImage: beforeImage2,
    afterImage: afterImage2,
  },
  {
    id: 3,
    treatmentName: 'DENTAL VENEERS',
    beforeImage: beforeImage3,
    afterImage: afterImage3,
  },
  {
    id: 4,
    treatmentName: 'INVISALIGN',
    beforeImage: beforeImage4,
    afterImage: afterImage4,
  },
];

// Real testimonial data from patients
const testimonialData = [
  {
    id: 1,
    name: 'بلقيس عبده',
    role: '1 review',
    image: avatarImage,
    text: ' نتقدم بجزيل الشكر والتقدير للدكتور العزيز محمد أبو سبعة ماشاء الله تبارك الله....قمة الاحترافية والأخلاق الطيبة أسأل الله تعالى أن يوفقه ويديم عليه الصحة والعافية. اللهم آمين',
  },
  {
    id: 2,
    name: 'Queen Qablaway',
    role: 'Local Guide',
    image: avatarImage,
    text: 'احسن عياده من ناحيه الاطباء شغلهم ممتاز جدا نظافه العيادات طاقم الاستقبال عياده راقيه فعلا',
  },
  {
    id: 3,
    name: 'West dammam',
    role: '1 review',
    image: avatarImage,
    text: '  👍👍👍 …بصراحة شغلهم مميز خاصة في علاج كبار السن والأطفال الدكتورة فوزية … والدكتوره شيماء',
  },
  {
    id: 4,
    name: 'Saleem Akhtar',
    role: '2 reviews',
    image: avatarImage,
    text: 'Good Doctor and well experience',
  },
  {
    id: 5,
    name: 'Masum',
    role: '1 review',
    image: avatarImage,
    text: 'Good treatment',
  },
  {
    id: 6,
    name: 'سما الود',
    role: '10 reviews',
    image: avatarImage,
    text: 'عيادة اسنان مرتبه والاسعار بطله مع جودة الشغل 👍🏻 الحمدلله لقيت دكاتره ثقه وجودة شغل و كذا اقدر اكمل علاج اسناني وانا متطمنه👍🏻 …',
  },
  {
    id: 7,
    name: 'خالد احمد',
    role: 'Local Guide',
    image: avatarImage,
    text: 'اشكر الاستقبال والدكتوره شيماء المساعده احترافيه شغلها ممتاز وواضح الخبره والمتابعه الدقيقه من قبل الدكتور جزاه الله خير الجزاء',
  },
  {
    id: 8,
    name: 'Abeer',
    role: 'Local Guide',
    image: avatarImage,
    text: 'خدمة ممتازة و نظام الاستقبال الموظفات السعوديه قمة في الروعه والأخلاق ربي يسعدهم اشتغلت عند الطبيب محمد السوداني شغال ممتاز ونظافه انصح بالشغل عندهم',
  },
  {
    id: 9,
    name: 'AA NA',
    role: '1 review',
    image: avatarImage,
    text: 'يوجد فيه دكتور / رامز السباعي شغله ممتاز وأخلاقه ممتازه جداً',
  },
  {
    id: 10,
    name: 'Seeta Aldossary',
    role: '1 review',
    image: avatarImage,
    text: 'جيت على مدح الناس للدكتور محمد ابو سبع عاجزة عن شكري له صراحه لي شهرين اتألم مع اني رحت عيادة بس ماعرفوا علاجي هو الوحيد اللي اكتشف سبب الالم وتخيلوا بدون بنج وربي يشهد علي اشتغل على 3 من أسناني حشو وتنظيف وسد فراغات ولثتي كانت ملتهبه علمني كيف استخدم الفرشاة وغير معجوني يارب لك الحمد الالم اللي كنت اقوم من نومي مفزوعه راح وصرت اكل عادي واكلت ايسكريم كمان اجرب الله يسعده وربي يده خفيفة ماتحسين وسريع ونظيف شغله دكتور يخاف ربه في شغله من زمان عن دكتور كذا صراحه وربي يشهد علي شكرا دكتور محمد ابوسبع',
  },
  {
    id: 11,
    name: 'مشاري مساعد',
    role: '2 reviews',
    image: avatarImage,
    text: 'ماشاء الله نظافة ودقة بالعمل واتقان  وتعامل راقي جدا من الجميع شكرا دكتور محمد ابو سبعه عمله ممتااز جدا واحترافي تعجز كلماتي عن شكرك',
  },
  {
    id: 12,
    name: 'Albndry',
    role: '1 review',
    image: avatarImage,
    text: 'عيادة جدا جميله الدكاتره ولا اروع شغلهم جدا ممتاز واسلوبهم جميل واهم شيء النظافه مهتمين بأدق التفاصيل ما شاء الله',
  }
];

const Reviews = ({ id }) => {
  const { t, i18n } = useTranslation();
  // State for tracking which images show the "after" state
  const [showAfter, setShowAfter] = useState({});
  // State for testimonials carousel
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  // Calculate number of full pages (2 testimonials per page)
  const pairsCount = Math.floor(testimonialData.length / 2);
  // Add an extra page if there's an odd number of testimonials
  const hasPartialPage = testimonialData.length % 2 !== 0;
  const maxTestimonialIndex = pairsCount + (hasPartialPage ? 0 : -1);

  // Initialize the showAfter state with all treatments set to "before"
  useEffect(() => {
    const initialAfterState = {};
    treatmentData.forEach(treatment => {
      initialAfterState[treatment.id] = false;
    });
    setShowAfter(initialAfterState);
  }, []);

  // Toggle between before and after images
  const toggleBeforeAfter = (id) => {
    setShowAfter(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Navigate testimonials with circular functionality
  const goToPrevTestimonial = () => {
    setActiveTestimonial(prev => prev === 0 ? maxTestimonialIndex : prev - 1);
  };

  const goToNextTestimonial = () => {
    setActiveTestimonial(prev => prev === maxTestimonialIndex ? 0 : prev + 1);
  };

  return (
    <ReviewsSection id={id}>
      <Container>
        <ReviewsTitle $dir={i18n.dir()}>
          <span className="trusted-text">{t('home.reviews.title.trusted')}</span>{' '}
          <span className="care-text">{t('home.reviews.title.care')}</span>
        </ReviewsTitle>
        
        <BeforeAfterGrid>
          {treatmentData.map((treatment) => (
            <BeforeAfterCard key={treatment.id}>
              <CardImage 
                src={showAfter[treatment.id] ? treatment.afterImage : treatment.beforeImage} 
                alt={`${treatment.treatmentName} ${showAfter[treatment.id] ? (i18n.language === 'en' ? 'After' : 'بعد') : (i18n.language === 'en' ? 'Before' : 'قبل')} - ${i18n.language === 'en' ? 'National Medical Elite Dental Clinic' : 'عيادة النخبة الوطنية الطبية'}`}
                data-alt-key={`reviews.treatment.${treatment.id}.${showAfter[treatment.id] ? 'after' : 'before'}.alt`}
                data-alt-en={`${treatment.treatmentName} ${showAfter[treatment.id] ? 'After' : 'Before'} - National Medical Elite Dental Clinic`}
                data-alt-ar={`${treatment.treatmentName} ${showAfter[treatment.id] ? 'بعد' : 'قبل'} - عيادة النخبة الوطنية الطبية`}
              />
              <ToggleContainer onClick={() => toggleBeforeAfter(treatment.id)}>
                <ToggleSlider $position={showAfter[treatment.id] ? 'after' : 'before'} />
                {i18n.dir() === 'rtl' ? (
                  <>
                    <ToggleOption $isActive={!showAfter[treatment.id]}>{t('home.reviews.toggle.before')}</ToggleOption>
                    <ToggleOption $isActive={showAfter[treatment.id]}>{t('home.reviews.toggle.after')}</ToggleOption>
                  </>
                ) : (
                  <>
                    <ToggleOption $isActive={showAfter[treatment.id]}>{t('home.reviews.toggle.after')}</ToggleOption>
                    <ToggleOption $isActive={!showAfter[treatment.id]}>{t('home.reviews.toggle.before')}</ToggleOption>
                  </>
                )}
              </ToggleContainer>
            </BeforeAfterCard>
          ))}
        </BeforeAfterGrid>
        
        <TestimonialsContainer>
          <TestimonialsHeader $dir={i18n.dir()}>{t('home.reviews.testimonials')}</TestimonialsHeader>
          
          <TestimonialsWrapper>
            <TestimonialsTrack $activeIndex={activeTestimonial} $dir={i18n.dir()}>
              {[...Array(pairsCount + (hasPartialPage ? 1 : 0))].map((_, pairIndex) => (
                <TestimonialItem key={pairIndex} $dir={i18n.dir()}>
                  <div className="testimonial-wrapper" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  {testimonialData.slice(pairIndex * 2, pairIndex * 2 + 2).map(testimonial => (
                    <div key={testimonial.id} style={{ display: 'flex', marginBottom: '20px', width: '100%' }}>
                      <TestimonialProfile $dir={i18n.dir()}>
                        <ProfileImage 
                          src={testimonial.image} 
                          alt={`${testimonial.name}, ${testimonial.role} - ${i18n.language === 'en' ? 'Patient Testimonial' : 'شهادة مريض'}`}
                          data-alt-key={`reviews.testimonial.${testimonial.id}.alt`}
                          data-alt-en={`${testimonial.name}, ${testimonial.role} - Patient Testimonial`}
                          data-alt-ar={`${testimonial.name}, ${testimonial.role} - شهادة مريض`}
                        />
                      </TestimonialProfile>
                      <TestimonialContent>
                        <ClientName>{testimonial.name}</ClientName>
                        <TestimonialText>{testimonial.text}</TestimonialText>
                      </TestimonialContent>
                    </div>
                  ))}
                  </div>
                </TestimonialItem>
              ))}
            </TestimonialsTrack>
          </TestimonialsWrapper>
          
          <NavigationControls $dir={i18n.dir()}>
            <ProgressIndicators>
              {[...Array(pairsCount + (hasPartialPage ? 1 : 0))].map((_, index) => (
                <ProgressDot 
                  key={index} 
                  $isActive={activeTestimonial === index} 
                />
              ))}
            </ProgressIndicators>
            <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 0 20px' }}>
              <NavButton onClick={goToPrevTestimonial} style={{ order: i18n.dir() === 'rtl' ? 2 : 1 }}>
                ←
              </NavButton>
              <NavButton onClick={goToNextTestimonial} style={{ order: i18n.dir() === 'rtl' ? 1 : 2 }}>
                →
              </NavButton>
            </div>
          </NavigationControls>
        </TestimonialsContainer>
      </Container>
    </ReviewsSection>
  );
};

export default Reviews;
